import type { ErrorResponse } from "@shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { brigadeService } from "../services/brigade.service";
import type { CreateBrigadeRequest, ListBrigadesParams, UpdateBrigadeRequest } from "../types";

export const brigadeKeys = {
	all: ["brigades"] as const,
	lists: () => [...brigadeKeys.all, "list"] as const,
	list: (params: ListBrigadesParams) => [...brigadeKeys.lists(), params] as const,
	details: () => [...brigadeKeys.all, "detail"] as const,
	detail: (id: string) => [...brigadeKeys.details(), id] as const,
};

export function useBrigadeList(params: ListBrigadesParams) {
	return useQuery({
		queryKey: brigadeKeys.list(params),
		queryFn: () => brigadeService.list(params),
		enabled: !!params.organizationId,
	});
}

export function useBrigade(id: string) {
	return useQuery({
		queryKey: brigadeKeys.detail(id),
		queryFn: () => brigadeService.getById(id),
		enabled: !!id,
	});
}

export function useCreateBrigade() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateBrigadeRequest) => brigadeService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: brigadeKeys.lists() });
			message.success("Brigada muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Brigada saqlashda xatolik";
			message.error(msg);
		},
	});
}

export function useUpdateBrigade() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateBrigadeRequest }) =>
			brigadeService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: brigadeKeys.lists() });
			queryClient.invalidateQueries({ queryKey: brigadeKeys.detail(variables.id) });
			message.success("Brigada muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Brigadani yangilashda xatolik";
			message.error(msg);
		},
	});
}
